export type PushSupport = "supported" | "unsupported" | "ios-install-required" | "denied";

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const navigatorWithStandalone = navigator as NavigatorWithStandalone;
  return (
    /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
    navigatorWithStandalone.standalone === true
  );
}

function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const navigatorWithStandalone = navigator as NavigatorWithStandalone;
  return (
    navigatorWithStandalone.standalone === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches)
  );
}

export function getPushSupport(): PushSupport {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "unsupported";
  if (window.isSecureContext === false) return "unsupported";

  if (isIosDevice() && !isStandaloneDisplay()) return "ios-install-required";

  if (
    typeof Notification === "undefined" ||
    !("serviceWorker" in navigator) ||
    typeof PushManager === "undefined"
  ) {
    return "unsupported";
  }

  return Notification.permission === "denied" ? "denied" : "supported";
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const trimmed = value.trim();
  if (!trimmed || !/^[A-Za-z0-9_-]+={0,2}$/u.test(trimmed)) {
    throw new TypeError("The push application key is not valid base64url.");
  }

  const unpadded = trimmed.replace(/=+$/u, "");
  if (unpadded.length % 4 === 1 || typeof atob !== "function") {
    throw new TypeError("The push application key is not valid base64url.");
  }

  const padding = "=".repeat((4 - (unpadded.length % 4)) % 4);
  let decoded: string;
  try {
    decoded = atob(`${unpadded.replace(/-/gu, "+").replace(/_/gu, "/")}${padding}`);
  } catch {
    throw new TypeError("The push application key is not valid base64url.");
  }

  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

async function readyPushManager(): Promise<PushManager | null> {
  if (getPushSupport() !== "supported") return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager;
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  const pushManager = await readyPushManager();
  return pushManager ? pushManager.getSubscription() : null;
}

export async function createPushSubscription(publicKey: string): Promise<PushSubscription> {
  const support = getPushSupport();
  if (support !== "supported") {
    throw new Error(`Push notifications are unavailable: ${support}.`);
  }

  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  if (applicationServerKey.byteLength !== 65 || applicationServerKey[0] !== 4) {
    throw new TypeError("The push application key must be an uncompressed P-256 public key.");
  }

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notification permission was denied."
        : "Notification permission was not granted."
    );
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey
  });
}

export async function removePushSubscription(
  subscription: PushSubscription | null
): Promise<boolean> {
  return subscription ? subscription.unsubscribe() : true;
}
